import * as React from 'react';
import { observer } from 'mobx-react';
import { Validator } from 'class-validator';
import autobind from 'autobind-decorator';

import { DataLocalService } from 'src/core/services/data-local.service';
import { Column, DataTableView, ToolbarVisibility } from 'src/core/ui/data-table.view';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { AutocompleteView } from 'src/core/ui/autocomplete/autocomplete.view';
import { TextView } from 'src/core/ui/text.view';
import { AutocompleteListDataService } from 'src/core/ui/autocomplete/autocomplete.data';

interface Props extends ComponentViewProps<TourItineraryDateItem> {
	mode: ControlMode;
	getAgeBandTag: (item: AgeBandItem) => string;
	onChangeAgeBandValidity: (value: boolean) => void;
	ensureOccupancyConstraints: (newPaxPerBooking?: number) => void;
}

@observer
export class TourItineraryDateAgeBandConfig extends React.Component<Props, {}> {
	private ageTypeDataService: AutocompleteListDataService;
	private validator: Validator;

	constructor(props: Readonly<Props>) {
		super(props);

		this.ageTypeDataService = AutocompleteListDataService.make();
		this.validator = new Validator();
	}

	private ageBandOptions: AgeBandItem[] = [
		{
			label: 'Adult',
			type: 0,
			minAge: 13,
			maxAge: 99
		},
		{
			label: 'Child',
			type: 1,
			minAge: 3,
			maxAge: 12
		},
		{
			label: 'Infant',
			type: 2,
			minAge: 0,
			maxAge: 2
		}
	];

	private getAgeTypesRequet(): DropdownRequest<AutocompleteSummary> {
		return {
			query: async (q?: string): Promise<QueryResult<AutocompleteSummary>> =>
				new Promise<QueryResult<AutocompleteSummary>>((resolve, reject) => {
					this.ageTypeDataService.addFilter('concept', 'eq', 'contract');
					this.ageTypeDataService.addFilter('key', 'eq', 'age-type');
					this.ageTypeDataService.addSorting('value', 'asc', true);
					this.ageTypeDataService.searchKeywords = q;
					this.ageTypeDataService.pageSize = 100;

					return this.ageTypeDataService
						.load(query => query)
						.then((items) => {
							resolve({
								items: items,
								totalCount: this.ageTypeDataService.totalPages
							});
						})
						.catch(reject)
						.then(() => {
							this.props.setBusy(false);
						});
				}),
			dataSet: this.ageTypeDataService
		};
	}

	@autobind
	private async handleCreateAgeBand(): Promise<AgeBandItem> {
		const { model, onChangeAgeBandValidity } = this.props;
		const ageBands: AgeBandItem[] = model.dynamicProperties.ageBands;
		const unusedAgeBands = this.ageBandOptions.filter(m => ageBands.every(p => p.type !== m.type));
		if (unusedAgeBands.length === 0) {
			return null;
		}

		const newAgeBand: AgeBandItem = { ...unusedAgeBands[0] };
		this.handleUpdateCustomOccupancies(newAgeBand, 'create');
		ageBands.push(newAgeBand);
		if (ageBands.length === 1) {
			onChangeAgeBandValidity(true);
		}

		return newAgeBand;
	}

	@autobind
	private beforeDeleteAgeBands(items: AgeBandItem[]): AgeBandItem[] {
		this.props.onChangeAgeBandValidity(false); // other tabs could be in unconsistent state
		return items;
	}

	@autobind
	private afterDeleteAgeBands(items: AgeBandItem[]): AgeBandItem[] {
		const { model, ensureOccupancyConstraints, onChangeAgeBandValidity } = this.props;

		ensureOccupancyConstraints();
		if (model.dynamicProperties.ageBands.length > 0) {
			onChangeAgeBandValidity(true);
		}

		return items;
	}

	private handleUpdateCustomOccupancies(item: AgeBandItem, action: 'create' | 'delete'): void {
		const { model, getAgeBandTag } = this.props;
		const paxConfig: ItineraryDatePaxConfig = model.dynamicProperties.paxConfig;
		const tag = getAgeBandTag(item);

		for (let occupancy of paxConfig.occupancies) {
			switch (action) {
				case 'create':
					occupancy[tag] = '0';
					break;
				case 'delete':
					occupancy[tag] = undefined;
					break;
			}
		}
	}

	@autobind
	private async isValidAgeBand(item: AgeBandItem): Promise<string[]> {
		const errors = [];
		if (this.validator.isEmpty(item.type)) {
			errors.push('type is required');
		}
		if (this.validator.isEmpty(item.minAge)) {
			errors.push('min age is required');
		}
		if (this.validator.isEmpty(item.maxAge)) {
			errors.push('max age is required');
		}
		if (!this.validator.isNumberString(item.minAge.toString())) {
			errors.push('min age must be a number');
		}
		if (!this.validator.isNumberString(item.maxAge.toString())) {
			errors.push('max age must be a number');
		}

		return errors;
	}

	render(): React.ReactNode {
		if (this.ageTypeDataService == null) {
			return null;
		}

		const { model, mode, setBusy } = this.props;
		const dataSet = new DataLocalService(model.dynamicProperties.ageBands);
		const ageTypeRequest = this.getAgeTypesRequet();

		const columns: Column[] = [
			{
				title: 'Type',
				key: 'type',
				dataIndex: 'type',
				editable: (text: number, record: AgeBandItem, index: number, isSelected: boolean) => {
					if (isSelected) {
						return (
							<DropdownView
								required
								disabled={mode === 'display'}
								label={null}
								model={record.type}
								options={this.ageBandOptions}
								keyField="type"
								textField="label"
								onChanged={(newValue) => {
									if (newValue != null) {
										record.type = newValue.type;
									}
								}}
							/>
						);
					}

					const option = this.ageBandOptions.find(o => o.type == text);
					return option ? option.label : text;
				}
			},
			{
				title: 'Label',
				key: 'label',
				dataIndex: 'label',
				editable: (text: string, record: AgeBandItem, index: number, isSelected: boolean) => {
					if (isSelected) {
						return (
							<AutocompleteView
								required
								disabled={mode === 'display'}
								label={null}
								model={record.label}
								request={ageTypeRequest}
								onChanged={(newValue) => {
									if (newValue != null) {
										record.label = newValue.value;
									}
								}}
							/>
						);
					}

					return text != null ? text : '';
				}
			},
			{
				title: 'Min Age',
				key: 'minAge',
				dataIndex: 'minAge',
				editable: (text: string, record: AgeBandItem, index: number, isSelected: boolean) => {
					if (isSelected) {
						return (
							<TextView
								mode="edit"
								label={null}
								targetType="int+"
								value={record.minAge}
								onChanged={(newValue) => {
									record.minAge = newValue;
								}}
							/>
						);
					}

					return text != null ? text : '';
				}
			},
			{
				title: 'Max Age',
				key: 'maxAge',
				dataIndex: 'maxAge',
				editable: (text: string, record: AgeBandItem, index: number, isSelected: boolean) => {
					if (isSelected) {
						return (
							<TextView
								mode="edit"
								label={null}
								targetType="int+"
								value={record.maxAge}
								onChanged={newVal => {
									record.maxAge = newVal;
								}}
							/>
						);
					}

					return text != null ? text : '';
				}
			}
		];

		return (
			<DataTableView
				rowKey={(record: AgeBandItem) => `${record.type}-${record.minAge}-${record.maxAge}`}
				mode={mode}
				setBusy={setBusy}
				columns={columns}
				model={dataSet}
				isValidItem={this.isValidAgeBand}
				createNewItem={this.handleCreateAgeBand}
				beforeDeleteItem={this.beforeDeleteAgeBands}
				afterDeleteItem={this.afterDeleteAgeBands}
				hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination | ToolbarVisibility.hideFooter}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/products/tour-itineraries/tour-itinerary-date/form/parts/tour-itinerary-date-age-band-config.tsx